import { useMemo, useState, useEffect, useCallback, Fragment } from 'react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Link } from 'react-router-dom'
import {
	Check,
	CheckCheck,
	FileText,
	MapPin,
	Play,
	Volume2,
	Download,
	Loader2,
	AlertCircle,
	Pin,
	PinOff,
	MoreVertical,
	Reply,
	RotateCcw,
	UserPlus,
	X,
	Video,
	ImageOff,
} from 'lucide-react'
import { render as renderFlexMessage } from 'flex-render'

import type { FlexContainer } from 'flex-render'

import { cn } from '@/common/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/common/ui/avatar'
import { Button } from '@/common/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { ImageWithFallback } from '@/common/ui/image-with-fallback'

import { SystemMessage } from '../SystemMessage'
import { ImagePreviewModal, VideoPreviewModal } from '../ImagePreviewModal'
import { MessageTextRenderer } from '../MessageTextRenderer'

import { ChatMessageResponse, ChatMessageType } from '../../sse'

/** Same family as LinkPlayerDialog: 0 + 8–9 digits, not a substring of a longer number */
const THAI_MOBILE_IN_TEXT = /(?<![0-9])0[0-9]{8,9}(?![0-9])/

export type LinkPlayerFromMessagePayload =
	| { playerId: string }
	| { phone: string }

function getFirstThaiPhoneInText(text: string | undefined): string | null {
	if (!text) return null
	const m = text.match(THAI_MOBILE_IN_TEXT)
	return m?.[0] ?? null
}

interface MessageBubbleProps {
	message: ChatMessageResponse
	showAvatar?: boolean
	avatarUrl?: string
	avatarName?: string
	isPending?: boolean
	isFailed?: boolean
	isHighlighted?: boolean
	onPinMessage?: (messageId: string, isPinned: boolean) => void
	onReplyMessage?: (message: ChatMessageResponse) => void
	onRetryMessage?: (clientId: string) => void
	onRemoveMessage?: (clientId: string) => void
	onNavigateToMessage?: (messageId: string) => void
	onAvatarClick?: () => void
	roomId?: string
	/**
	 * Room-level allow (e.g. no player linked). The button only appears when this is
	 * true and the incoming text message contains a Thai mobile number (0 + 8–9 digits).
	 */
	enableLinkPlayerFromMessage?: boolean
	onLinkPlayerFromMessage?: (payload: LinkPlayerFromMessagePayload) => void
	isLinkingPlayerFromMessage?: boolean
}

function getReplyPreview(
	message: ChatMessageResponse['replyToMessage'],
): string {
	if (!message) return ''
	switch (message.messageType) {
		case ChatMessageType.TEXT:
			return message.text?.slice(0, 50) || ''
		case ChatMessageType.STICKER:
			return '[สติกเกอร์]'
		case ChatMessageType.IMAGE:
			return '[รูปภาพ]'
		default:
			return '[ข้อความ]'
	}
}

export function MessageBubble({
	message,
	showAvatar = true,
	avatarUrl,
	avatarName,
	isPending = false,
	isFailed = false,
	isHighlighted = false,
	onPinMessage,
	onReplyMessage,
	onRetryMessage,
	onRemoveMessage,
	onNavigateToMessage,
	onAvatarClick,
	roomId,
	enableLinkPlayerFromMessage = false,
	onLinkPlayerFromMessage,
	isLinkingPlayerFromMessage = false,
}: MessageBubbleProps) {
	const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
	const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null)

	// System messages render as centered notifications
	if (message.messageType === ChatMessageType.SYSTEM) {
		return <SystemMessage message={message} />
	}

	const isOutgoing = message.direction === 'OUTGOING'
	const firstPlayerLink = message.playerLinks?.[0]
	const phoneFromText =
		message.messageType === ChatMessageType.TEXT
			? getFirstThaiPhoneInText(message.text)
			: null
	const isLinkPlayerFromMessageEnabled =
		enableLinkPlayerFromMessage && !!phoneFromText
	const showLinkPlayerButton =
		isLinkPlayerFromMessageEnabled && !!onLinkPlayerFromMessage && !isOutgoing
	const time = format(new Date(message.createdAt), 'HH:mm', { locale: th })

	return (
		<>
			<div
				data-message-id={message.id}
				className={cn(
					'group flex gap-2 rounded-lg transition-colors',
					isOutgoing ? 'flex-row-reverse' : 'flex-row',
					isPending && 'opacity-70',
					isHighlighted && 'animate-shake-highlight',
				)}
			>
				{/* Avatar */}
				{showAvatar && !isOutgoing && (
					<Button
						type="button"
						onClick={onAvatarClick}
						variant="ghost"
						size="icon"
						className="rounded-full"
					>
						<Avatar className="h-8 w-8">
							<AvatarImage src={avatarUrl} alt={avatarName} />
							<AvatarFallback className="text-xs">
								{avatarName?.slice(0, 2).toUpperCase() || 'U'}
							</AvatarFallback>
						</Avatar>
					</Button>
				)}

				{/* Message Content */}
				<div
					className={cn(
						'flex max-w-[70%] flex-col gap-1',
						isOutgoing ? 'items-end' : 'items-start',
					)}
				>
					{/* Reply-to Message Preview */}
					{message.replyToMessage && (
						<button
							type="button"
							className={cn(
								'flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-1.5 text-xs transition-colors',
								isOutgoing ? 'ml-auto' : 'mr-auto',
								onNavigateToMessage &&
									'cursor-pointer hover:bg-muted/70 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
							)}
							onClick={() => onNavigateToMessage?.(message.replyToMessage!.id)}
							disabled={!onNavigateToMessage}
						>
							<Reply className="h-3 w-3 shrink-0 text-muted-foreground" />
							<div className="min-w-0 text-left">
								<span className="font-medium text-muted-foreground">
									{message.replyToMessage.direction === 'INCOMING'
										? 'ลูกค้า'
										: message.replyToMessage.sentByOperator?.username ||
											'Operator'}
								</span>
								<p className="truncate text-muted-foreground">
									{getReplyPreview(message.replyToMessage)}
								</p>
							</div>
						</button>
					)}

					<div className="relative">
						<MessageContent
							message={message}
							isOutgoing={isOutgoing}
							onImageClick={setPreviewImageUrl}
							onVideoClick={setPreviewVideoUrl}
							isPending={isPending}
							isFailed={isFailed}
						/>

						{/* Context Menu */}
						{(onPinMessage || onReplyMessage) && roomId && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="absolute right-1 top-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
										onClick={(e) => e.stopPropagation()}
									>
										<MoreVertical className="h-3 w-3" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									{onReplyMessage && (
										<DropdownMenuItem onClick={() => onReplyMessage(message)}>
											<Reply className="mr-2 h-4 w-4" />
											ตอบกลับ
										</DropdownMenuItem>
									)}
									{onPinMessage && (
										<DropdownMenuItem
											onClick={() =>
												onPinMessage(message.id, !message.isPinned)
											}
										>
											{message.isPinned ? (
												<>
													<PinOff className="mr-2 h-4 w-4" />
													ยกเลิกปักหมุด
												</>
											) : (
												<>
													<Pin className="mr-2 h-4 w-4" />
													ปักหมุดข้อความ
												</>
											)}
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>

					{showLinkPlayerButton && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="mt-1 h-7 gap-1.5 px-2 text-xs"
							onClick={(e) => {
								e.stopPropagation()
								if (!onLinkPlayerFromMessage) return
								if (firstPlayerLink) {
									onLinkPlayerFromMessage({
										playerId: firstPlayerLink.playerId,
									})
									return
								}
								if (phoneFromText) {
									onLinkPlayerFromMessage({ phone: phoneFromText })
								}
							}}
							disabled={isLinkingPlayerFromMessage}
						>
							{isLinkingPlayerFromMessage ? (
								<Loader2 className="h-3 w-3 shrink-0 animate-spin" />
							) : (
								<UserPlus className="h-3 w-3 shrink-0" />
							)}
							เชื่อมต่อผู้เล่น
						</Button>
					)}

					{/* Time and Status */}
					<div
						className={cn(
							'flex items-center gap-1 text-xs text-muted-foreground',
							isOutgoing ? 'flex-row-reverse' : 'flex-row',
						)}
					>
						<span>{time}</span>
						{isOutgoing && (
							<MessageStatus
								isRead={message.isRead}
								usedReplyToken={message.usedReplyToken}
								isPending={isPending}
								isFailed={isFailed}
							/>
						)}
						{message.sentByOperator && (
							<span className="text-xs opacity-70">
								โดย {message.sentByOperator.username}
							</span>
						)}
						{/* Retry and Remove buttons for failed messages */}
						{isFailed && message.clientId && (
							<div className="flex items-center gap-1">
								{onRetryMessage && (
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													className="h-5 w-5 text-muted-foreground hover:text-foreground"
													onClick={() => onRetryMessage(message.clientId!)}
												>
													<RotateCcw className="h-3 w-3" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p>ลองส่งอีกครั้ง</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								)}
								{onRemoveMessage && (
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													className="h-5 w-5 text-muted-foreground hover:text-destructive"
													onClick={() => onRemoveMessage(message.clientId!)}
												>
													<X className="h-3 w-3" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p>ลบข้อความ</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Image Preview Modal */}
			<ImagePreviewModal
				imageUrl={previewImageUrl}
				onClose={() => setPreviewImageUrl(null)}
			/>

			{/* Video Preview Modal */}
			<VideoPreviewModal
				videoUrl={previewVideoUrl}
				onClose={() => setPreviewVideoUrl(null)}
			/>
		</>
	)
}

const PLAYER_LINK_REGEX = /(\[player:[^\]]+\])/g
const PLAYER_LINK_CAPTURE = /\[player:([^:]+):([^\]]+)\]/

export function MessageTextContent({ text }: { text?: string }) {
	if (!text) return null

	const parts = text.split(PLAYER_LINK_REGEX)
	const hasLinks = parts.length > 1

	if (!hasLinks) {
		return <>{text}</>
	}

	return (
		<>
			{parts.map((part, i) => {
				const match = part.match(PLAYER_LINK_CAPTURE)
				if (match) {
					const [, shortUserId, playerId] = match
					return (
						<Link
							key={i}
							to={`/operator/get-player-list/player-detail/${playerId}`}
							className="font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid"
						>
							{shortUserId}
						</Link>
					)
				}
				return <Fragment key={i}>{part}</Fragment>
			})}
		</>
	)
}

interface MessageContentProps {
	message: ChatMessageResponse
	isOutgoing: boolean
	onImageClick?: (imageUrl: string) => void
	onVideoClick?: (videoUrl: string) => void
	isPending?: boolean
	isFailed?: boolean
}

function MessageContent({
	message,
	isOutgoing,
	onImageClick,
	onVideoClick,
	isFailed,
}: MessageContentProps) {
	const baseClass = cn(
		'rounded-2xl px-4 py-2',
		isOutgoing
			? 'bg-primary text-primary-foreground rounded-br-sm'
			: 'bg-muted rounded-bl-sm',
		isFailed && 'ring-2 ring-destructive',
	)

	switch (message.messageType) {
		case ChatMessageType.TEXT:
			return (
				<div className={baseClass}>
					<p className="whitespace-pre-wrap break-words">
						<MessageTextRenderer text={message.text} />
					</p>
				</div>
			)

		case ChatMessageType.STICKER:
			return (
				<StickerMessage
					packageId={message.stickerPackageId}
					stickerId={message.stickerId}
				/>
			)

		case ChatMessageType.IMAGE:
			return (
				<ImageMessage
					originalUrl={message.imageOriginalUrl}
					previewUrl={message.imagePreviewUrl}
					onImageClick={onImageClick}
				/>
			)

		case ChatMessageType.VIDEO:
			return (
				<VideoMessage
					originalUrl={message.videoOriginalUrl}
					previewUrl={message.videoPreviewUrl}
					duration={message.videoDuration}
					onVideoClick={onVideoClick}
				/>
			)

		case ChatMessageType.AUDIO:
			return (
				<AudioMessage
					url={message.audioOriginalUrl}
					duration={message.audioDuration}
				/>
			)

		case ChatMessageType.FILE:
			return (
				<FileMessage
					url={message.fileUrl}
					name={message.fileName}
					size={message.fileSize}
					isOutgoing={isOutgoing}
				/>
			)

		case ChatMessageType.LOCATION:
			return (
				<LocationMessage
					title={message.locationTitle}
					address={message.locationAddress}
					latitude={message.locationLatitude}
					longitude={message.locationLongitude}
					isOutgoing={isOutgoing}
				/>
			)

		case ChatMessageType.FLEX:
			return (
				<FlexMessage
					content={message.flexContents}
					altText={message.flexAltText}
				/>
			)

		case ChatMessageType.TEMPLATE:
			return (
				<div className={baseClass}>
					<p className="text-sm opacity-70">
						[Template Message] {message.templateAltText || 'ไม่สามารถแสดงได้'}
					</p>
				</div>
			)

		default:
			return (
				<div className={baseClass}>
					<p className="text-sm opacity-70">[ข้อความไม่รองรับ]</p>
				</div>
			)
	}
}

interface MessageStatusProps {
	isRead: boolean
	usedReplyToken: boolean
	isPending?: boolean
	isFailed?: boolean
}

function MessageStatus({
	isRead,
	usedReplyToken,
	isPending,
	isFailed,
}: MessageStatusProps) {
	if (isFailed) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="flex items-center">
							<AlertCircle className="h-3 w-3 text-destructive" />
						</span>
					</TooltipTrigger>
					<TooltipContent>
						<p>ส่งไม่สำเร็จ</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		)
	}

	if (isPending) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="flex items-center">
							<Loader2 className="h-3 w-3 animate-spin" />
						</span>
					</TooltipTrigger>
					<TooltipContent>
						<p>กำลังส่ง...</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		)
	}

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="flex items-center">
						{isRead ? (
							<CheckCheck className="h-3 w-3 text-blue-500" />
						) : (
							<Check className="h-3 w-3" />
						)}
					</span>
				</TooltipTrigger>
				<TooltipContent>
					<p>
						{isRead ? 'อ่านแล้ว' : 'ส่งแล้ว'}
						{usedReplyToken && ' (ฟรี)'}
					</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

interface StickerMessageProps {
	packageId?: string
	stickerId?: string
}

function StickerMessage({ packageId, stickerId }: StickerMessageProps) {
	// LINE sticker URL format
	const stickerUrl =
		packageId && stickerId
			? `https://stickershop.line-scdn.net/stickershop/v1/sticker/${stickerId}/android/sticker.png`
			: null

	if (!stickerUrl) {
		return (
			<div className="rounded-lg bg-muted p-4 text-center text-sm text-muted-foreground">
				[สติกเกอร์ไม่สามารถแสดงได้]
			</div>
		)
	}

	return (
		<img
			src={stickerUrl}
			alt="Sticker"
			className="h-24 w-24 object-contain"
			onError={(e) => {
				e.currentTarget.src = ''
				e.currentTarget.alt = '[สติกเกอร์]'
			}}
		/>
	)
}

interface ImageMessageProps {
	originalUrl?: string
	previewUrl?: string
	onImageClick?: (imageUrl: string) => void
}

function ImageMessage({
	originalUrl,
	previewUrl,
	onImageClick,
}: ImageMessageProps) {
	const displayUrl = previewUrl || originalUrl
	const fullUrl = originalUrl || displayUrl

	return (
		<Button
			variant="ghost"
			onClick={() => fullUrl && onImageClick?.(fullUrl)}
			className="block h-auto overflow-hidden rounded-lg p-0 transition-transform hover:scale-[1.02]"
		>
			<ImageWithFallback
				src={displayUrl}
				alt="Image"
				className="h-[200px] w-[200px] max-w-full object-contain transition-opacity hover:opacity-90"
				fallback={<ImageOff className="h-12 w-12 text-muted-foreground" />}
				loading="lazy"
			/>
		</Button>
	)
}

interface VideoMessageProps {
	originalUrl?: string
	previewUrl?: string
	duration?: number
	onVideoClick?: (videoUrl: string) => void
}

/**
 * Hook to generate video thumbnail from first frame
 */
function useVideoThumbnail(
	videoUrl: string | undefined,
	serverPreviewUrl: string | undefined,
) {
	const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState(false)

	const generateThumbnail = useCallback((url: string): Promise<string> => {
		return new Promise((resolve, reject) => {
			const video = document.createElement('video')
			video.crossOrigin = 'anonymous'
			video.preload = 'metadata'
			video.muted = true

			const canvas = document.createElement('canvas')
			const ctx = canvas.getContext('2d')

			video.onloadeddata = () => {
				// Seek to first frame
				video.currentTime = 0.1
			}

			video.onseeked = () => {
				if (!ctx) {
					reject(new Error('Canvas context not available'))
					return
				}

				canvas.width = video.videoWidth
				canvas.height = video.videoHeight
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

				try {
					const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
					resolve(dataUrl)
				} catch {
					reject(new Error('Failed to generate thumbnail'))
				}
			}

			video.onerror = () => {
				reject(new Error('Failed to load video'))
			}

			// Timeout after 10 seconds
			const timeout = setTimeout(() => {
				reject(new Error('Thumbnail generation timeout'))
			}, 10000)

			video.onloadeddata = () => {
				clearTimeout(timeout)
				video.currentTime = 0.1
			}

			video.src = url
			video.load()
		})
	}, [])

	useEffect(() => {
		// If server provides preview URL, use it
		if (serverPreviewUrl) {
			setThumbnailUrl(serverPreviewUrl)
			setError(false)
			return
		}

		// If no video URL, nothing to do
		if (!videoUrl) {
			setThumbnailUrl(null)
			return
		}

		// Try to generate thumbnail from first frame
		setIsLoading(true)
		setError(false)

		generateThumbnail(videoUrl)
			.then((dataUrl) => {
				setThumbnailUrl(dataUrl)
				setError(false)
			})
			.catch(() => {
				setThumbnailUrl(null)
				setError(true)
			})
			.finally(() => {
				setIsLoading(false)
			})
	}, [videoUrl, serverPreviewUrl, generateThumbnail])

	return { thumbnailUrl, isLoading, error }
}

function VideoMessage({
	originalUrl,
	previewUrl,
	duration,
	onVideoClick,
}: VideoMessageProps) {
	const { thumbnailUrl, isLoading } = useVideoThumbnail(originalUrl, previewUrl)

	const durationText = useMemo(() => {
		if (!duration) return null
		const seconds = Math.floor(duration / 1000)
		const mins = Math.floor(seconds / 60)
		const secs = seconds % 60
		return `${mins}:${secs.toString().padStart(2, '0')}`
	}, [duration])

	if (!originalUrl) {
		return (
			<div className="rounded-lg bg-muted p-4 text-center text-sm text-muted-foreground">
				[วิดีโอไม่สามารถแสดงได้]
			</div>
		)
	}

	// Fallback element for video preview
	const videoFallback = isLoading ? (
		<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
	) : (
		<Video className="h-12 w-12 text-muted-foreground" />
	)

	return (
		<Button
			variant="ghost"
			onClick={() => onVideoClick?.(originalUrl)}
			className="relative block h-auto cursor-pointer overflow-hidden rounded-lg p-0 transition-transform hover:scale-[1.02]"
		>
			<ImageWithFallback
				src={thumbnailUrl || undefined}
				alt="Video preview"
				className="h-[200px] w-[200px] object-cover"
				fallback={videoFallback}
				loading="lazy"
			/>
			{/* Play button overlay */}
			<div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40">
				<div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90">
					<Play className="h-6 w-6 text-black" />
				</div>
			</div>
			{durationText && (
				<span className="absolute bottom-2 right-2 rounded bg-black/70 px-2 py-0.5 text-xs text-white">
					{durationText}
				</span>
			)}
		</Button>
	)
}

interface AudioMessageProps {
	url?: string
	duration?: number
}

function AudioMessage({ url, duration }: AudioMessageProps) {
	const durationText = useMemo(() => {
		if (!duration) return null
		const seconds = Math.floor(duration / 1000)
		const mins = Math.floor(seconds / 60)
		const secs = seconds % 60
		return `${mins}:${secs.toString().padStart(2, '0')}`
	}, [duration])

	if (!url) {
		return (
			<div className="rounded-lg bg-muted p-4 text-center text-sm text-muted-foreground">
				[เสียงไม่สามารถเล่นได้]
			</div>
		)
	}

	return (
		<div className="flex items-center gap-3 rounded-lg bg-muted px-4 py-3">
			<Volume2 className="h-5 w-5 text-muted-foreground" />
			<audio controls className="h-8 max-w-[200px]">
				<source src={url} />
			</audio>
			{durationText && (
				<span className="text-xs text-muted-foreground">{durationText}</span>
			)}
		</div>
	)
}

interface FileMessageProps {
	url?: string
	name?: string
	size?: number
	isOutgoing: boolean
}

function FileMessage({ url, name, size, isOutgoing }: FileMessageProps) {
	const sizeText = useMemo(() => {
		if (!size) return null
		if (size < 1024) return `${size} B`
		if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
		return `${(size / 1024 / 1024).toFixed(1)} MB`
	}, [size])

	return (
		<div
			className={cn(
				'flex items-center gap-3 rounded-lg px-4 py-3',
				isOutgoing ? 'bg-primary/80 text-primary-foreground' : 'bg-muted',
			)}
		>
			<FileText className="h-8 w-8 shrink-0" />
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium">{name || 'ไฟล์'}</p>
				{sizeText && <p className="text-xs opacity-70">{sizeText}</p>}
			</div>
			{url && (
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 shrink-0"
					asChild
				>
					<a href={url} target="_blank" rel="noopener noreferrer" download>
						<Download className="h-4 w-4" />
					</a>
				</Button>
			)}
		</div>
	)
}

interface LocationMessageProps {
	title?: string
	address?: string
	latitude?: number
	longitude?: number
	isOutgoing: boolean
}

function LocationMessage({
	title,
	address,
	latitude,
	longitude,
	isOutgoing,
}: LocationMessageProps) {
	const mapsUrl =
		latitude && longitude
			? `https://www.google.com/maps?q=${latitude},${longitude}`
			: null

	return (
		<div
			className={cn(
				'overflow-hidden rounded-lg',
				isOutgoing ? 'bg-primary/80 text-primary-foreground' : 'bg-muted',
			)}
		>
			{/* Map Preview */}
			{latitude && longitude && (
				<a
					href={mapsUrl || '#'}
					target="_blank"
					rel="noopener noreferrer"
					className="block"
				>
					<img
						src={`https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=15&size=300x150&markers=color:red%7C${latitude},${longitude}&key=${import.meta.env.VITE_GOOGLE_MAPS_KEY || ''}`}
						alt="Location"
						className="h-32 w-full object-cover"
						onError={(e) => {
							e.currentTarget.style.display = 'none'
						}}
					/>
				</a>
			)}

			<div className="flex items-start gap-2 p-3">
				<MapPin className="mt-0.5 h-4 w-4 shrink-0" />
				<div className="min-w-0 flex-1">
					{title && <p className="text-sm font-medium">{title}</p>}
					{address && <p className="text-xs opacity-70">{address}</p>}
					{mapsUrl && (
						<a
							href={mapsUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="mt-1 inline-block text-xs underline"
						>
							เปิดใน Google Maps
						</a>
					)}
				</div>
			</div>
		</div>
	)
}

interface FlexMessageProps {
	content?: Record<string, unknown>
	altText?: string
}

function FlexMessage({ content, altText }: FlexMessageProps) {
	const renderedHtml = useMemo(() => {
		if (!content) return null
		try {
			return renderFlexMessage(content as FlexContainer)
		} catch {
			return null
		}
	}, [content])

	if (!renderedHtml) {
		return (
			<div className="rounded-lg bg-muted px-4 py-2 text-sm text-muted-foreground">
				[Flex Message] {altText || 'ไม่สามารถแสดงได้'}
			</div>
		)
	}

	return (
		<div className="w-[300px] max-w-full rounded-lg border bg-card">
			<div
				className="flex-preview"
				dangerouslySetInnerHTML={{ __html: renderedHtml }}
			/>
		</div>
	)
}
